// Filtering for the document history, shared by the mobile screen and the web
// page so both offer the same facets and agree on what each one means.

function str(v) {
  return (v ?? '').toString().trim();
}

// A document's tiers are objects ({tierId, tierName, value}); everywhere they
// are shown or matched it is the values that matter.
export function tierValues(doc) {
  return (Array.isArray(doc?.tiers) ? doc.tiers : [])
    .map((t) => str(t?.value))
    .filter(Boolean);
}

// The location line under a file name: upload type, package, then the tier
// path. Spreading the raw tier objects into a join() rendered them as
// "[object Object]", so this is the one way both clients build that line.
export function documentLocationLabel(doc) {
  const parts = [str(doc?.uploadType), str(doc?.package || doc?.packageName), ...tierValues(doc)];
  return parts.filter(Boolean).join(' · ') || 'Project Home';
}

function isRealTag(v) {
  return !!v && v.toLowerCase() !== 'none';
}

// The user's document tags - the ones chosen at upload from the list defined
// on the Settings tab.
//
// NOT `doc.tags`: that is the raw blob TAG RECORD ({project, supplier, block,
// level, checklist...}), an object, and reading it first turned every tag into
// the string "[object Object]". hubDocuments already extracts the real tags
// into `documentTags`, splitting the `checklist` blob tag that the upload
// writes from metadata - that is the field to read.
export function documentTags(doc) {
  // `tags` comes LAST and only counts when it is an array or a string - the
  // shape a state comment record uses. As the blob tag record object it
  // matches neither branch and is skipped, which is the whole point.
  const candidates = [doc?.documentTags, doc?.context?.tags, doc?.checklist, doc?.tags?.checklist, doc?.tags];
  for (const raw of candidates) {
    if (Array.isArray(raw)) {
      const values = raw.map(str).filter(isRealTag);
      if (values.length) return values;
      continue;
    }
    // Some upload paths carry them as one delimited string.
    const text = typeof raw === 'string' ? raw : '';
    const values = text.split(/[|,]/).map((s) => s.trim()).filter(isRealTag);
    if (values.length) return values;
  }
  return [];
}

export function documentPackage(doc) {
  return str(doc?.package || doc?.packageName);
}

export function documentType(doc) {
  return str(doc?.uploadType || doc?.documentType);
}

const ALL = '';

// The options offered for each facet, taken from the documents actually
// present rather than a fixed list - a filter that offers a value nothing
// matches, or hides one that exists, is worse than no filter.
export function buildDocumentFacets(documents = []) {
  const packages = new Set();
  const types = new Set();
  const tags = new Set();
  for (const doc of documents) {
    const pkg = documentPackage(doc);
    if (pkg) packages.add(pkg);
    const type = documentType(doc);
    if (type) types.add(type);
    for (const tag of documentTags(doc)) tags.add(tag);
  }
  const sorted = (set) => [...set].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  return { packages: sorted(packages), types: sorted(types), tags: sorted(tags) };
}

// Everything about a document someone might half-remember when hunting for it:
// its name, where it was filed, who supplied it, what it was tagged with. The
// dropdowns answer "show me all the drawings"; this answers "where is that thing
// about the lintels", which is how people actually look for a file they have
// already seen.
// What an agent said about the document, as one searchable string.
//
// Every analysis is paid for with a model call, and until now the one search
// box where that would obviously pay off could not see it: the list matched on
// file name, package, type, location and tags only. So a document whose summary
// said "cavity barrier" did not come up when you typed "cavity barrier", and
// the thing you had already paid for was doing no work here.
//
// Reads the built-in analysis and every custom agent result, since which one
// ran depends on the document. Agent names are included too - "what did the
// standards agent look at" is a real question people ask of the list.
const AI_TEXT_MAX = 4000;

export function documentAiText(doc) {
  const parts = [];
  const take = (value) => {
    if (Array.isArray(value)) value.forEach(take);
    else if (value && typeof value === 'object') { /* structured results are read field by field below */ }
    else if (value) parts.push(str(value));
  };

  const fromResult = (result) => {
    if (!result || typeof result !== 'object') return;
    take(result.title);
    take(result.summary);
    take(result.keyPoints);
    take(result.identifiedAs);
  };

  take(doc?.ai?.summary);
  fromResult(doc?.ai?.result);

  for (const run of Array.isArray(doc?.agentResults) ? doc.agentResults : []) {
    take(run?.agentName || run?.agentId);
    fromResult(run?.result || run);
  }

  // Bounded: a spec extraction can run to hundreds of key points, and this is
  // re-derived on every keystroke of the search box.
  return parts.filter(Boolean).join(' ').slice(0, AI_TEXT_MAX);
}

function searchableText(doc) {
  return [
    str(doc?.fileName),
    documentPackage(doc),
    documentType(doc),
    documentLocationLabel(doc),
    str(doc?.supplier),
    ...tierValues(doc),
    ...documentTags(doc),
    documentAiText(doc),
  ].filter(Boolean).join(' ').toLowerCase();
}

// Every word must match, in any order and anywhere in the document's details, so
// "lintel drawing" finds a drawing about lintels rather than everything carrying
// either word. Matching the phrase as typed would fail on the far more common
// "block a plan" when the document reads "plan ... Block A".
function matchesSearch(doc, terms) {
  if (!terms.length) return true;
  const haystack = searchableText(doc);
  return terms.every((term) => haystack.includes(term));
}

export function filterDocuments(documents = [], { package: pkg = ALL, type = ALL, tag = ALL, search = '' } = {}) {
  const wantPkg = str(pkg);
  const wantType = str(type);
  const wantTag = str(tag);
  const terms = str(search).toLowerCase().split(/\s+/).filter(Boolean);
  if (!wantPkg && !wantType && !wantTag && !terms.length) return documents;
  return documents.filter((doc) => {
    if (wantPkg && documentPackage(doc) !== wantPkg) return false;
    if (wantType && documentType(doc) !== wantType) return false;
    if (wantTag && !documentTags(doc).includes(wantTag)) return false;
    if (!matchesSearch(doc, terms)) return false;
    return true;
  });
}

export function hasActiveFilters({ package: pkg = ALL, type = ALL, tag = ALL, search = '' } = {}) {
  return !!(str(pkg) || str(type) || str(tag) || str(search));
}

export const EMPTY_FILTERS = { package: ALL, type: ALL, tag: ALL, search: '' };
